
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Typography, Paper, CircularProgress,
    Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
    TextField, ButtonBase, Button, Collapse, IconButton, Tooltip
} from '@mui/material';
import dayjs from 'dayjs';
import { calcMonthOccupancy } from '../lib/occupancy';
import InventoryIcon from '@mui/icons-material/Inventory';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DashboardSidebar from '../components/DashboardSidebar';
import { useReservationData } from '../context/ReservationDataContext';
import { fetchDispatchReservations, fetchVehicleTypePricesV2 } from '../api/dispatchService';
import { isVacantStatus } from '../constants/calendar';
import { useAuth } from '../components/AuthContext';

const StockCheck: React.FC = () => {
    const { user } = useAuth();
    const isAdmin = user?.level === 1;
    const { reservations, vehicles, vehicleTypes, isLoadingReservations, isLoadingVehicles, isLoadingVehicleTypes } = useReservationData() as any;

    const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
        start: dayjs().format('YYYY-MM-DD'),
        end: dayjs().add(14, 'day').format('YYYY-MM-DD')
    });

    const [revenueMonth, setRevenueMonth] = useState(dayjs().format('YYYY-MM'));
    const [revenueOpen, setRevenueOpen] = useState(true);

    // 売上用予約データ（API直接取得 — Contextのカレンダー範囲に依存しない）
    const [revenueReservations, setRevenueReservations] = useState<any[]>([]);
    const [isLoadingRevenue, setIsLoadingRevenue] = useState(false);

    // 在庫CSV出力中フラグ
    const [isExportingStockCsv, setIsExportingStockCsv] = useState(false);

    // 月間売上CSV出力中フラグ
    const [isExportingRevenueCsv, setIsExportingRevenueCsv] = useState(false);

    const loadRevenueReservations = useCallback(async (month: string) => {
        setIsLoadingRevenue(true);
        try {
            const center = dayjs(month + '-01');
            // 選択月 ±12ヶ月分を取得
            const startDate = center.subtract(12, 'month').startOf('month').format('YYYY-MM-DD');
            const endDate = center.add(12, 'month').endOf('month').format('YYYY-MM-DD');
            const data = await fetchDispatchReservations({ start_date: startDate, end_date: endDate });
            setRevenueReservations(data || []);
        } catch (e) {
            console.error('[StockCheck] 売上用予約取得エラー:', e);
        } finally {
            setIsLoadingRevenue(false);
        }
    }, []);

    useEffect(() => {
        loadRevenueReservations(revenueMonth);
    }, [revenueMonth, loadRevenueReservations]);

    useEffect(() => {
        document.title = '在庫管理 | レンタカー予約カレンダー';
    }, []);

    const loading = isLoadingReservations || isLoadingVehicles || isLoadingVehicleTypes;

    // 車種別月間売上集計
    const revenueMatrix = useMemo(() => {
        const center = dayjs(revenueMonth + '-01');
        const months: dayjs.Dayjs[] = [];
        for (let i = -12; i <= 12; i++) {
            months.push(center.add(i, 'month'));
        }

        const vehiclesList = Object.values(vehicles) as any[];

        // 月ごとに「その月のリソース」に数える車両か（台数の頭数）
        // active_from〜active_to の期間と対象月が重なるかだけで決める。
        // is_active=false（意図的に予約を止めている予備車）・out_of_use もリソースに含める
        // （2026-09-22 前田さん指示。以前は除外していた）。日数按分は lib/occupancy.ts の実質台数で行う
        const isVehicleActiveInMonth = (v: any, month: dayjs.Dayjs) => {
            const mStart = month.startOf('month');
            const mEnd = month.endOf('month');
            // active_fromがあれば、月末より後なら非アクティブ
            if (v.active_from) {
                const from = dayjs(v.active_from);
                if (from.isAfter(mEnd)) return false;
            }
            // active_toがあれば、月初より前なら非アクティブ
            if (v.active_to) {
                const to = dayjs(v.active_to);
                if (to.isBefore(mStart)) return false;
            }
            return true;
        };

        // 有効ステータス (Dashboardと同じ許可リスト方式)
        // 確定=1/booked, 貸出済み=2/done, 利用中=3/in_use
        const INCLUDED_STATUSES = new Set(['1', '2', '3', 'booked', 'done', 'in_use']);
        const isIncludedStatus = (status: any) => INCLUDED_STATUSES.has(String(status).toLowerCase());

        // 媒体分類ヘルパー
        const SELF_MEDIA = ['lp[en]', 'lp[tw]', 'lp[kr]', 'lp[ja]', 'TEL'];
        const OTHER_PORTAL = ['エアトリ', 'rcom'];
        const classifyMedia = (media: string | null | undefined): string => {
            if (!media) return 'その他';
            const m = media.trim();
            if (m === 'JA' || m.toLowerCase() === 'jalan') return 'JA';
            if (m === 'レンナビ' || m.toLowerCase() === 'rennavi') return 'レンナビ';
            if (m === 'okiren' || m.toLowerCase() === 'okiren') return 'okiren';
            if (SELF_MEDIA.includes(m)) return '自社';
            if (OTHER_PORTAL.includes(m)) return 'その他媒体';
            return 'その他';
        };
        const MEDIA_KEYS = ['JA', 'レンナビ', 'okiren', '自社', 'その他媒体', 'その他'] as const;
        type MediaKey = typeof MEDIA_KEYS[number];
        const emptyMediaCounts = (): Record<MediaKey, number> => ({ JA: 0, 'レンナビ': 0, okiren: 0, '自社': 0, 'その他媒体': 0, 'その他': 0 });

        const includedReservations = revenueReservations.filter((r: any) => isIncludedStatus(r.status));

        const matrix = vehicleTypes.map((type: any) => {
            const typeVehicles = vehiclesList.filter((v: any) => v.car_type_id === type.id);

            const monthlyData = months.map((month) => {
                const mStart = month.startOf('month');
                const mEnd = month.endOf('month');

                const activeTypeVehicles = typeVehicles.filter((v: any) => isVehicleActiveInMonth(v, month));
                const activeCount = activeTypeVehicles.length;

                let total = 0;
                let count = 0;
                const mediaCounts = emptyMediaCounts();

                revenueReservations.forEach((r: any) => {
                    const vehicle = vehiclesList.find((v: any) => v.id === r.vehicle_id);
                    if (!vehicle || vehicle.car_type_id !== type.id) return;
                    if (!isIncludedStatus(r.status)) return;

                    const rStart = dayjs(r.start_date);
                    if (rStart.isBefore(mStart) || rStart.isAfter(mEnd)) return;

                    count++;
                    const media = r.detail?.contact_media ?? r.contact_media ?? '';
                    const key = classifyMedia(media);
                    mediaCounts[key as MediaKey]++;

                    const price = Number(r.detail?.sum_price ?? r.sum_price ?? 0);
                    if (price > 0) {
                        total += price;
                    }
                });

                // 稼働率計算（lib/occupancy.ts）: 通常予約の占有日数 ÷ (Σ稼働可能日数 − 米軍・その他の日数)
                // 実質台数 = 分母 ÷ 月日数（月の途中で入った/抜けた車は日数按分。2026-09-22）
                // 米軍・その他の予約は車両の利用期間外と同じ扱い＝稼働していない（2026-09-19）
                const { occupiedDays, excludedDays, totalCapacityDays, effectiveVehicles, occupancyRate } = calcMonthOccupancy(
                    activeTypeVehicles,
                    includedReservations,
                    month,
                );

                return {
                    total,
                    count,
                    activeCount,
                    effectiveVehicles,
                    // 平均/台 も実質台数で割る（2026-09-22）
                    avg: effectiveVehicles > 0 ? Math.round(total / effectiveVehicles) : 0,
                    occupancyRate,
                    occupiedDays,
                    excludedDays,
                    totalCapacityDays,
                    mediaCounts,
                };
            });

            // 現在月のアクティブ台数（ヘッダー表示用）
            const currentActiveCount = typeVehicles.filter((v: any) => isVehicleActiveInMonth(v, dayjs())).length;

            return {
                type,
                vehicleCount: currentActiveCount,
                months: monthlyData,
            };
        }).filter((row: any) => row.months.some((m: any) => m.activeCount > 0));

        // 月合計行
        const totalRow = months.map((_m, mIdx) => {
            let total = 0;
            let count = 0;
            let activeCount = 0;
            let effectiveVehicles = 0;
            let occupiedDays = 0;
            let excludedDays = 0;
            let totalCapacityDays = 0;
            const mediaCounts = emptyMediaCounts();
            matrix.forEach((row: any) => {
                total += row.months[mIdx].total;
                count += row.months[mIdx].count;
                activeCount += row.months[mIdx].activeCount;
                effectiveVehicles += row.months[mIdx].effectiveVehicles;
                occupiedDays += row.months[mIdx].occupiedDays;
                excludedDays += row.months[mIdx].excludedDays;
                totalCapacityDays += row.months[mIdx].totalCapacityDays;
                MEDIA_KEYS.forEach((k) => { mediaCounts[k] += row.months[mIdx].mediaCounts[k]; });
            });
            const occupancyRate = totalCapacityDays > 0 ? (occupiedDays / totalCapacityDays) * 100 : 0;
            return {
                total, count, activeCount, effectiveVehicles, mediaCounts,
                avg: effectiveVehicles > 0 ? Math.round(total / effectiveVehicles) : 0,
                occupancyRate: Math.min(occupancyRate, 100),
                occupiedDays, excludedDays, totalCapacityDays,
            };
        });

        return { months, matrix, totalRow };
    }, [revenueReservations, vehicles, vehicleTypes, revenueMonth]);

    const stockMatrix = useMemo(() => {
        const start = dayjs(dateRange.start);
        const end = dayjs(dateRange.end);
        const days: any[] = [];
        let curr = start;
        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
            days.push(curr);
            curr = curr.add(1, 'day');
        }

        const vehiclesList = Object.values(vehicles) as any[];

        // Filter valid vehicles
        const activeVehicles = vehiclesList.filter((v: any) =>
            v.is_active !== false &&
            !v.out_of_use
        );

        // Group by Vehicle Type
        const matrix = vehicleTypes.map((type: any) => {
            const typeVehicles = activeVehicles.filter((v: any) => v.car_type_id === type.id);
            const dailyCounts = days.map((day: any) => {
                const dayStart = day.startOf('day');
                const dayEnd = day.endOf('day');

                let availableCount = 0;
                typeVehicles.forEach((vehicle: any) => {
                    const hasReservation = reservations.some((r: any) => {
                        if (r.vehicle_id !== vehicle.id) return false;

                        // 空き扱いステータス（backend「予約済みとしてカウント」OFF）のみ除外。
                        // それ以外（未知の新ステータス含む）はすべて busy 扱い（安全側）。
                        // ※ 旧実装は Number(r.status) で [4,5,6,7] と比較していたが、
                        //   r.status はコード文字列のため NaN になり除外が一切効いていなかった。
                        if (isVacantStatus(r.status)) return false;

                        const rStart = dayjs(r.start_date);
                        const rEnd = dayjs(r.end_date);

                        // Overlap check
                        return rStart.isBefore(dayEnd) && rEnd.isAfter(dayStart);
                    });

                    if (!hasReservation) {
                        availableCount++;
                    }
                });
                return availableCount;
            });

            return {
                type,
                total: typeVehicles.length,
                counts: dailyCounts
            };
        });

        return { days, matrix };
    }, [reservations, vehicles, vehicleTypes, dateRange]);

    // 在庫CSV出力（本日から+9ヶ月、デイリー、車種別 総数/在庫/価格）
    const handleExportStockCsv = useCallback(async () => {
        setIsExportingStockCsv(true);
        try {
            const start = dayjs().startOf('day');
            const end = start.add(9, 'month');
            const startStr = start.format('YYYY-MM-DD');
            const endStr = end.format('YYYY-MM-DD');

            // 9ヶ月分の予約をAPI直接取得（Contextのカレンダー範囲に依存しない）
            const fetched: any[] = (await fetchDispatchReservations({ start_date: startStr, end_date: endStr })) || [];

            // 占有判定対象（在庫表と同じ基準: 空き扱いステータスを除外）の予約を vehicle_id でグルーピング
            const reservationsByVehicle = new Map<any, { start: dayjs.Dayjs; end: dayjs.Dayjs }[]>();
            fetched.forEach((r: any) => {
                if (isVacantStatus(r.status)) return;
                const list = reservationsByVehicle.get(r.vehicle_id) || [];
                list.push({ start: dayjs(r.start_date), end: dayjs(r.end_date) });
                reservationsByVehicle.set(r.vehicle_id, list);
            });

            // 価格v2 API は最大90日なので90日チャンクで取得して vehicle_type_id ごとにマージ
            const pricesByTypeId = new Map<string, Record<string, number>>();
            const PRICE_CHUNK_DAYS = 90;
            let chunkStart = start;
            while (chunkStart.isBefore(end) || chunkStart.isSame(end, 'day')) {
                let chunkEnd = chunkStart.add(PRICE_CHUNK_DAYS - 1, 'day');
                if (chunkEnd.isAfter(end)) chunkEnd = end;
                try {
                    const resp = await fetchVehicleTypePricesV2({
                        start_date: chunkStart.format('YYYY-MM-DD'),
                        end_date: chunkEnd.format('YYYY-MM-DD'),
                    });
                    const vtList: any[] = resp?.data?.vehicle_types || [];
                    vtList.forEach((vt: any) => {
                        const key = String(vt.id);
                        const existing = pricesByTypeId.get(key) || {};
                        pricesByTypeId.set(key, { ...existing, ...(vt.prices || {}) });
                    });
                } catch (e) {
                    console.warn('[StockCheck] 価格取得失敗（該当チャンクは空欄で続行）', chunkStart.format('YYYY-MM-DD'), chunkEnd.format('YYYY-MM-DD'), e);
                }
                chunkStart = chunkEnd.add(1, 'day');
            }

            const vehiclesList = Object.values(vehicles) as any[];

            // 日付配列（本日から+9ヶ月の同日まで）
            const days: dayjs.Dayjs[] = [];
            let curr = start;
            while (curr.isBefore(end) || curr.isSame(end, 'day')) {
                days.push(curr);
                curr = curr.add(1, 'day');
            }

            // 日次アクティブ判定（active_from/active_to が指す期間と当日が重なるか）
            const isActiveOnDay = (v: any, day: dayjs.Dayjs): boolean => {
                if (v.is_active === false || v.out_of_use) return false;
                if (v.active_from && dayjs(v.active_from).isAfter(day, 'day')) return false;
                if (v.active_to && dayjs(v.active_to).isBefore(day, 'day')) return false;
                return true;
            };

            // 対象期間中に1日でもアクティブな車両を持つ車種のみ列に含める
            const typeRows = (vehicleTypes as any[])
                .map((type: any) => {
                    const typeVehicles = vehiclesList.filter((v: any) => v.car_type_id === type.id);
                    const hasAnyActive = typeVehicles.some((v: any) => days.some(d => isActiveOnDay(v, d)));
                    return hasAnyActive ? { type, typeVehicles } : null;
                })
                .filter(Boolean) as { type: any; typeVehicles: any[] }[];

            // ヘッダー: 車種ごとに 総数 / 在庫 / 価格 の3列
            const labelOf = (type: any): string => String(type.name || type.label || type.code || `id:${type.id ?? ''}`);
            const header: string[] = ['日付'];
            typeRows.forEach(({ type }) => {
                const label = labelOf(type);
                header.push(`${label}総数`, `${label}在庫`, `${label}価格`);
            });

            // 各日 × 各車種で 総数 / 在庫 / 価格 を算出
            const rows = days.map((day) => {
                const dayStart = day.startOf('day');
                const dayEnd = day.endOf('day');
                const dayKey = day.format('YYYY-MM-DD');
                const cells: string[] = [dayKey];

                typeRows.forEach(({ type, typeVehicles }) => {
                    const activeVehicles = typeVehicles.filter((v: any) => isActiveOnDay(v, day));
                    const total = activeVehicles.length;

                    let available = 0;
                    activeVehicles.forEach((vehicle: any) => {
                        const list = reservationsByVehicle.get(vehicle.id) || [];
                        const occupied = list.some(({ start: rs, end: re }) => rs.isBefore(dayEnd) && re.isAfter(dayStart));
                        if (!occupied) available++;
                    });

                    const priceMap = pricesByTypeId.get(String(type.id)) || {};
                    const price = priceMap[dayKey];
                    const priceCell = price != null ? String(price) : '';

                    cells.push(String(total), String(available), priceCell);
                });

                return cells;
            });

            // CSV組み立て（ReservationListと同じBOM付きUTF-8）
            const escape = (val: any) => {
                const s = String(val).replace(/"/g, '""');
                return s.includes(',') || s.includes('\n') || s.includes('"') ? `"${s}"` : s;
            };
            const csvContent = [header, ...rows]
                .map(row => row.map(escape).join(','))
                .join('\n');

            const fileName = `stock_daily_${start.format('YYYYMMDD')}_${end.format('YYYYMMDD')}.csv`;
            const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error('[StockCheck] 在庫CSV出力エラー:', e);
            alert('CSV出力に失敗しました');
        } finally {
            setIsExportingStockCsv(false);
        }
    }, [vehicles, vehicleTypes]);

    // 月間売上CSV出力（現在の revenueMatrix をClaude分析用のロング形式で出力）
    // 1行 = (月 × 車種) の集計。単価・稼働率検証用に元指標もそのまま出力する。
    const handleExportRevenueCsv = useCallback(() => {
        setIsExportingRevenueCsv(true);
        try {
            const labelOf = (type: any): string => String(type.name || type.label || type.code || `id:${type.id ?? ''}`);

            const header = [
                '月',
                '車種',
                '車種コード',
                '種別',           // 個別 or 合計
                '合計売上_円',
                '件数',
                'アクティブ台数',
                '実質台数',
                '平均_円_per台',
                '稼働率_パーセント',
                '占有日数',
                '総容量日数',
                '稼働対象外日数',
                'JA件数',
                'レンナビ件数',
                'okiren件数',
                '自社件数',
                'その他媒体件数',
                'その他件数',
            ];

            const escape = (val: any) => {
                if (val == null) return '';
                const s = String(val).replace(/"/g, '""');
                return /[",\n]/.test(s) ? `"${s}"` : s;
            };

            const rows: (string | number)[][] = [];

            revenueMatrix.months.forEach((m: dayjs.Dayjs, mIdx: number) => {
                const monthKey = m.format('YYYY-MM');

                // 車種ごとの行
                revenueMatrix.matrix.forEach((row: any) => {
                    const md = row.months[mIdx];
                    const mc: Record<string, number> = md.mediaCounts || {};
                    rows.push([
                        monthKey,
                        labelOf(row.type),
                        row.type.code || '',
                        '個別',
                        md.total ?? 0,
                        md.count ?? 0,
                        md.activeCount ?? 0,
                        Number((md.effectiveVehicles ?? 0).toFixed(2)),
                        md.avg ?? 0,
                        md.activeCount > 0 ? Number(md.occupancyRate.toFixed(2)) : '',
                        md.occupiedDays ?? 0,
                        md.totalCapacityDays ?? 0,
                        md.excludedDays ?? 0,
                        mc.JA ?? 0,
                        mc['レンナビ'] ?? 0,
                        mc.okiren ?? 0,
                        mc['自社'] ?? 0,
                        mc['その他媒体'] ?? 0,
                        mc['その他'] ?? 0,
                    ]);
                });

                // 月合計行
                const totalMd = revenueMatrix.totalRow[mIdx];
                const totalMc: Record<string, number> = totalMd.mediaCounts || {};
                rows.push([
                    monthKey,
                    '合計',
                    '',
                    '合計',
                    totalMd.total ?? 0,
                    totalMd.count ?? 0,
                    totalMd.activeCount ?? 0,
                    Number((totalMd.effectiveVehicles ?? 0).toFixed(2)),
                    totalMd.avg ?? 0,
                    totalMd.activeCount > 0 ? Number(totalMd.occupancyRate.toFixed(2)) : '',
                    totalMd.occupiedDays ?? 0,
                    totalMd.totalCapacityDays ?? 0,
                    totalMd.excludedDays ?? 0,
                    totalMc.JA ?? 0,
                    totalMc['レンナビ'] ?? 0,
                    totalMc.okiren ?? 0,
                    totalMc['自社'] ?? 0,
                    totalMc['その他媒体'] ?? 0,
                    totalMc['その他'] ?? 0,
                ]);
            });

            const csvContent = [header, ...rows]
                .map(r => r.map(escape).join(','))
                .join('\n');

            const startMonth = revenueMatrix.months[0]?.format('YYYYMM') || '';
            const endMonth = revenueMatrix.months[revenueMatrix.months.length - 1]?.format('YYYYMM') || '';
            const fileName = `revenue_monthly_${startMonth}_${endMonth}.csv`;
            const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error('[StockCheck] 月間売上CSV出力エラー:', e);
            alert('CSV出力に失敗しました');
        } finally {
            setIsExportingRevenueCsv(false);
        }
    }, [revenueMatrix]);

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
            <DashboardSidebar />

            <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 }, overflow: 'auto' }}>
                <Box sx={{ mb: { xs: 2, md: 4 }, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5 }}>
                            在庫管理
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                            車種ごとの空き状況を確認します
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                            type="date"
                            label="開始日"
                            value={dateRange.start}
                            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            type="date"
                            label="終了日"
                            value={dateRange.end}
                            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                        <ButtonBase
                            sx={{
                                bgcolor: '#fff',
                                border: '1px solid #e2e8f0',
                                borderRadius: 2,
                                px: 2,
                                py: 1,
                                color: '#64748b',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                '&:hover': { bgcolor: '#f1f5f9', color: '#1e293b' }
                            }}
                        >
                            <RefreshIcon sx={{ fontSize: 18, mr: 0.5 }} />
                            更新
                        </ButtonBase>
                        <Tooltip title="本日から +9ヶ月のデイリー在庫を CSV 出力（車種別 総数 / 在庫 / 価格）" arrow>
                            <span>
                                <ButtonBase
                                    onClick={handleExportStockCsv}
                                    disabled={isExportingStockCsv || loading}
                                    sx={{
                                        bgcolor: '#fff',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: 2,
                                        px: 2,
                                        py: 1,
                                        color: '#7c3aed',
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                        '&:hover': { bgcolor: '#f5f3ff', color: '#6d28d9' },
                                        '&.Mui-disabled': { opacity: 0.5, cursor: 'not-allowed' }
                                    }}
                                >
                                    {isExportingStockCsv ? (
                                        <CircularProgress size={16} sx={{ mr: 0.5, color: '#7c3aed' }} />
                                    ) : (
                                        <FileDownloadIcon sx={{ fontSize: 18, mr: 0.5 }} />
                                    )}
                                    CSV出力 (9ヶ月)
                                </ButtonBase>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>

                <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                    {loading ? (
                        <Box sx={{ p: 8, textAlign: 'center' }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2, color: '#64748b' }}>在庫データを計算中...</Typography>
                        </Box>
                    ) : (
                        <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{
                                            bgcolor: '#f1f5f9',
                                            fontWeight: 800,
                                            width: 100,
                                            minWidth: 100,
                                            zIndex: 13,
                                            position: 'sticky',
                                            left: 0
                                        }}>
                                            車種 / 日付
                                        </TableCell>
                                        <TableCell sx={{
                                            bgcolor: '#f1f5f9',
                                            fontWeight: 800,
                                            textAlign: 'center',
                                            width: 40,
                                            minWidth: 40,
                                            zIndex: 13,
                                            position: 'sticky',
                                            left: 100
                                        }}>
                                            総数
                                        </TableCell>
                                        {stockMatrix.days.map((day: any, idx: number) => (
                                            <TableCell
                                                key={idx}
                                                sx={{
                                                    bgcolor: day.day() === 0 ? '#fee2e2' : day.day() === 6 ? '#e0f2fe' : '#f1f5f9',
                                                    fontWeight: 700,
                                                    textAlign: 'center',
                                                    minWidth: 35,
                                                    fontSize: '0.7rem',
                                                    px: 0.5
                                                }}
                                            >
                                                {day.format('M/D')}
                                                <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', opacity: 0.6, lineHeight: 1 }}>
                                                    ({['日', '月', '火', '水', '木', '金', '土'][day.day()]})
                                                </Typography>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {stockMatrix.matrix.map((row: any, rIdx: number) => (
                                        <TableRow key={rIdx} hover>
                                            <TableCell sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                bgcolor: '#fff',
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 1,
                                                fontSize: '0.8rem',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {row.type.name || row.type.label || row.type.code}
                                                <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                    {row.type.code}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                textAlign: 'center',
                                                fontWeight: 600,
                                                bgcolor: '#f8fafc',
                                                position: 'sticky',
                                                left: 100,
                                                zIndex: 1,
                                                fontSize: '0.8rem'
                                            }}>
                                                {row.total}
                                            </TableCell>
                                            {row.counts.map((count: number, cIdx: number) => (
                                                <TableCell
                                                    key={cIdx}
                                                    sx={{
                                                        textAlign: 'center',
                                                        fontWeight: 700,
                                                        color: count === 0 ? '#ef4444' : count <= 2 ? '#f59e0b' : '#10b981',
                                                        bgcolor: count === 0 ? 'rgba(239, 68, 68, 0.05)' : 'transparent',
                                                        fontSize: '0.9rem',
                                                        px: 0.5
                                                    }}
                                                >
                                                    {count}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>

                <Box sx={{ mt: 3, p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InventoryIcon sx={{ fontSize: 14 }} />
                        ※ 在庫数 = 「00:00 〜 23:59 まで一度も予約が入っていない車両」の合計台数です。
                        (利用外フラグのある車両は除外されています)
                    </Typography>
                </Box>

                {/* 車種別月間売上セクション（管理者のみ） */}
                {isAdmin && <Paper sx={{ mt: 4, borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                    <Box
                        sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: revenueOpen ? '1px solid #e2e8f0' : 'none', cursor: 'pointer' }}
                        onClick={() => setRevenueOpen(!revenueOpen)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUpIcon sx={{ color: '#7c3aed' }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                                車種別 月間売上
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                                type="month"
                                value={revenueMonth}
                                onChange={(e) => { e.stopPropagation(); setRevenueMonth(e.target.value); }}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 160 }}
                            />
                            <Tooltip title="表示中の±12ヶ月 × 車種ごとの合計/件数/台数/平均/稼働率/媒体別 を CSV 出力（Claude分析用）" arrow>
                                <span>
                                    <ButtonBase
                                        onClick={(e) => { e.stopPropagation(); handleExportRevenueCsv(); }}
                                        disabled={isExportingRevenueCsv || loading}
                                        sx={{
                                            bgcolor: '#fff',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: 2,
                                            px: 1.5,
                                            py: 0.75,
                                            color: '#7c3aed',
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            '&:hover': { bgcolor: '#f5f3ff', color: '#6d28d9' },
                                            '&.Mui-disabled': { opacity: 0.5, cursor: 'not-allowed' }
                                        }}
                                    >
                                        {isExportingRevenueCsv ? (
                                            <CircularProgress size={14} sx={{ mr: 0.5, color: '#7c3aed' }} />
                                        ) : (
                                            <FileDownloadIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                        )}
                                        分析用CSV
                                    </ButtonBase>
                                </span>
                            </Tooltip>
                            <IconButton size="small">
                                {revenueOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Box>
                    </Box>
                    <Collapse in={revenueOpen}>
                        {loading ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                        ) : (
                            <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: '#f1f5f9', fontWeight: 800, position: 'sticky', left: 0, zIndex: 13, minWidth: 90 }}>
                                                車種
                                            </TableCell>
                                            <TableCell sx={{ bgcolor: '#f1f5f9', fontWeight: 600, position: 'sticky', left: 90, zIndex: 13, minWidth: 55, fontSize: '0.72rem', color: '#64748b', px: 0.5 }}>
                                                項目
                                            </TableCell>
                                            {revenueMatrix.months.map((m: dayjs.Dayjs, idx: number) => {
                                                const isCurrentMonth = m.isSame(dayjs(), 'month');
                                                const isCenter = m.format('YYYY-MM') === revenueMonth;
                                                return (
                                                    <TableCell
                                                        key={idx}
                                                        sx={{
                                                            bgcolor: isCurrentMonth ? '#dbeafe' : isCenter ? '#f0fdf4' : '#f1f5f9',
                                                            fontWeight: isCurrentMonth ? 800 : 700,
                                                            textAlign: 'center',
                                                            minWidth: 80,
                                                            fontSize: '0.7rem',
                                                            px: 0.5,
                                                        }}
                                                    >
                                                        {m.format('YY/M')}月
                                                        {isCurrentMonth && (
                                                            <Typography variant="caption" display="block" sx={{ fontSize: '0.55rem', color: '#2563eb', lineHeight: 1 }}>当月</Typography>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {[...revenueMatrix.matrix, { type: { name: '合計', code: '' }, months: revenueMatrix.totalRow, _isTotal: true }].map((row: any, rIdx: number) => {
                                            const isTotal = row._isTotal;
                                            const stickyBg = isTotal ? '#f1f5f9' : '#fff';
                                            const fs = '0.72rem';
                                            const labelSx = { position: 'sticky' as const, left: 90, zIndex: 1, bgcolor: stickyBg, px: 0.5, py: 0.3, fontSize: fs, color: '#64748b', borderBottom: 'none' };
                                            const cellSx = (mIdx: number) => ({
                                                textAlign: 'right' as const, px: 0.5, py: 0.3, fontSize: fs, borderBottom: 'none',
                                                bgcolor: revenueMatrix.months[mIdx]?.isSame(dayjs(), 'month') ? (isTotal ? 'rgba(37,99,235,0.06)' : 'rgba(37,99,235,0.03)') : (isTotal ? '#f1f5f9' : 'transparent'),
                                            });
                                            const nameSx = { fontWeight: isTotal ? 800 : 600, color: '#1e293b', bgcolor: stickyBg, position: 'sticky' as const, left: 0, zIndex: 1, fontSize: '0.78rem', whiteSpace: 'nowrap' as const, borderBottom: 'none', verticalAlign: 'top' as const };

                                            return (
                                                <React.Fragment key={rIdx}>
                                                    {/* 合計 */}
                                                    <TableRow sx={{ borderTop: '2px solid #e2e8f0' }}>
                                                        <TableCell rowSpan={6} sx={nameSx}>
                                                            {row.type.name || row.type.label}
                                                            {row.type.code && <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.62rem' }}>{row.type.code}</Typography>}
                                                        </TableCell>
                                                        <TableCell sx={{ ...labelSx, fontWeight: 700, color: '#1e293b' }}>合計</TableCell>
                                                        {row.months.map((md: any, mIdx: number) => (
                                                            <TableCell key={mIdx} sx={{ ...cellSx(mIdx), fontWeight: 700, color: '#1e293b' }}>
                                                                {md.total > 0 ? `¥${(md.total / 10000).toFixed(1)}万` : '-'}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                    {/* 平均値 */}
                                                    <TableRow>
                                                        <TableCell sx={{ ...labelSx, color: '#7c3aed' }}>平均/台</TableCell>
                                                        {row.months.map((md: any, mIdx: number) => (
                                                            <TableCell key={mIdx} sx={{ ...cellSx(mIdx), color: '#7c3aed' }}>
                                                                {md.avg > 0 ? `¥${(md.avg / 10000).toFixed(1)}万` : '-'}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                    {/* 台数 */}
                                                    <TableRow>
                                                        <TableCell sx={labelSx}>台数</TableCell>
                                                        {row.months.map((md: any, mIdx: number) => (
                                                            <TableCell key={mIdx} sx={{ ...cellSx(mIdx), color: '#64748b' }}>
                                                                {md.activeCount || '-'}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                    {/* 実質台数（稼働可能日数の按分。2026-09-22） */}
                                                    <TableRow>
                                                        <TableCell sx={labelSx}>実質台数</TableCell>
                                                        {row.months.map((md: any, mIdx: number) => (
                                                            <TableCell key={mIdx} sx={{ ...cellSx(mIdx), color: '#64748b' }}>
                                                                {md.activeCount > 0 ? md.effectiveVehicles.toFixed(1) : '-'}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                    {/* 件数 */}
                                                    <TableRow>
                                                        <TableCell sx={labelSx}>件数</TableCell>
                                                        {row.months.map((md: any, mIdx: number) => (
                                                            <TableCell key={mIdx} sx={{ ...cellSx(mIdx), color: '#94a3b8' }}>
                                                                {md.count || '-'}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                    {/* 媒体別 */}
                                                    <TableRow>
                                                        <TableCell sx={{ ...labelSx, fontSize: '0.65rem' }}>媒体別</TableCell>
                                                        {row.months.map((md: any, mIdx: number) => {
                                                            const mc = md.mediaCounts || {};
                                                            const parts = [mc.JA, mc['レンナビ'], mc.okiren, mc['自社'], mc['その他媒体'], mc['その他']];
                                                            const hasAny = parts.some((v: number) => v > 0);
                                                            return (
                                                                <TableCell key={mIdx} sx={{ ...cellSx(mIdx), fontSize: '0.65rem' }}>
                                                                    {hasAny ? (
                                                                        <Tooltip title={`JA:${mc.JA||0} / レンナビ:${mc['レンナビ']||0} / okiren:${mc.okiren||0} / 自社:${mc['自社']||0} / 他媒体:${mc['その他媒体']||0} / その他:${mc['その他']||0}`} arrow>
                                                                            <span style={{ color: '#475569' }}>
                                                                                {parts.map((v: number) => v || '-').join(' / ')}
                                                                            </span>
                                                                        </Tooltip>
                                                                    ) : '-'}
                                                                </TableCell>
                                                            );
                                                        })}
                                                    </TableRow>
                                                    {/* 稼働率 */}
                                                    <TableRow>
                                                        <TableCell sx={{ ...labelSx, fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>稼働率</TableCell>
                                                        {row.months.map((md: any, mIdx: number) => (
                                                            <TableCell key={mIdx} sx={{
                                                                ...cellSx(mIdx),
                                                                fontWeight: 600,
                                                                borderBottom: '2px solid #e2e8f0',
                                                                color: md.occupancyRate >= 80 ? '#10b981' : md.occupancyRate >= 50 ? '#f59e0b' : '#ef4444',
                                                            }}>
                                                                {md.activeCount > 0 ? `${md.occupancyRate.toFixed(1)}%` : '-'}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        <Box sx={{ p: 1.5, borderTop: '1px solid #e2e8f0' }}>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                ※ 合計 = 予約のsum_price合計（貸出開始日ベース）。平均 = 合計 ÷ 実質台数。キャンセル等除外。<br />
                                ※ 台数 = 有効期間が月と重なる車両の頭数（非アクティブ・利用外も含む）。実質台数 = 各車両の稼働可能日数（有効期間 − 米軍・その他の日数）÷ 月日数 の合計。<br />
                                ※ 媒体の表示順: JA / レンナビ / okiren / 自社 / その他媒体 / その他。その他媒体 = エアトリ、rcom。その他 = その他全て。<br />
                                ※ 稼働率 = 占有日数 ÷ (実質台数 × 月日数)。予約種別が「米軍」「その他」の予約は利用期間外の扱いで、占有にも容量にも数えません。
                            </Typography>
                        </Box>
                    </Collapse>
                </Paper>}
            </Box>
        </Box>
    );
};

export default StockCheck;
